import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { getFirestore } from "firebase/firestore";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { auth } from "../../firebase/firebaseConfig";
import { queryKeys } from "../../queries/queryKeys";
import { guardarCierreCaja } from "../../utils/rutaCierresCaja";
import { calcularGastosDelDia, obtenerGastosDelDiaDeRuta } from "../../utils/rutaGastos";
import { calcularSaldoActualRuta } from "../../utils/rutaMovimientos";
import type {
  AnyObject,
  UseCambiarContrasenaHandlerParams,
  UseCierreCajaHandlerParams,
  UseCierreCajaHandlerReturn,
  UseEstadisticasPageMetricsParams,
} from "./types";

export const calcularTotalesRutas = (rutasData: AnyObject[] | null) => {
  return (
    rutasData?.reduce(
      (totales, ruta) => {
        totales.saldoRutas += calcularSaldoActualRuta(ruta);
        totales.ultimoSaldo += Number(ruta.historialSaldos ?? 0);
        return totales;
      },
      { saldoRutas: 0, ultimoSaldo: 0 },
    ) ?? { saldoRutas: 0, ultimoSaldo: 0 }
  );
};

export const calcularGananciasRutas = (saldoRutas: number, ultimoSaldo: number): number => {
  return saldoRutas - ultimoSaldo;
};

export const obtenerSaldoMasNuevo = (usuarioRuta: AnyObject | null): number => {
  return Number(usuarioRuta?.historialSaldos ?? 0);
};

export const calcularGanancias = (usuarioRuta: AnyObject | null): number => {
  return calcularSaldoActualRuta(usuarioRuta) - Number(usuarioRuta?.historialSaldos ?? 0);
};

export const puedeCambiarContrasena = (
  usuarioRuta: AnyObject | null,
  userData: AnyObject | null,
): boolean => {
  return Boolean(usuarioRuta?.isAutorized || userData);
};

export const useCambiarContrasenaHandler = ({
  usuarioRuta,
  userData,
  setverCambiarContrasenaModal,
  setVerModalNoautorizado,
}: UseCambiarContrasenaHandlerParams) => {
  return useCallback(() => {
    if (puedeCambiarContrasena(usuarioRuta, userData)) {
      setverCambiarContrasenaModal(true);
    } else {
      setVerModalNoautorizado(true);
    }
  }, [setVerModalNoautorizado, setverCambiarContrasenaModal, userData, usuarioRuta]);
};

const getFechaHoy = (): string => format(new Date(), "dd/MM/yyyy");

export const useEstadisticasPageMetrics = ({
  usuarioRuta,
  rutasData,
}: UseEstadisticasPageMetricsParams) => {
  const usuarioRutaAdminUid = usuarioRuta?.adminUid;
  const usuarioRutaUid = usuarioRuta?.uid;
  const gastosHoyLegacy = calcularGastosDelDia(usuarioRuta?.historialGastos);

  // Alta frecuencia (cada gasto registrado lo altera); staleTime corto
  // porque es dinero mostrado en pantalla durante la operacion del dia.
  const { data: gastosHoy = gastosHoyLegacy } = useQuery({
    enabled: Boolean(usuarioRutaAdminUid && usuarioRutaUid),
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      try {
        const db = getFirestore();
        return await obtenerGastosDelDiaDeRuta({
          adminUid: usuarioRutaAdminUid as string,
          db,
          rutaUid: usuarioRutaUid as string,
        });
      } catch (error) {
        console.error("Error fetching gastos del dia desde Firebase:", error);
        return gastosHoyLegacy;
      }
    },
    queryKey: queryKeys.gastosDelDia(usuarioRutaAdminUid, usuarioRutaUid, getFechaHoy()),
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return useMemo(() => {
    const { saldoRutas, ultimoSaldo } = calcularTotalesRutas(rutasData);
    const gananciasRutas = calcularGananciasRutas(saldoRutas, ultimoSaldo);
    const saldoActual = calcularSaldoActualRuta(usuarioRuta);
    const saldoMasNuevo = obtenerSaldoMasNuevo(usuarioRuta);
    const ganancias = calcularGanancias(usuarioRuta);

    return {
      gastosHoy,
      gananciasRutas,
      ganancias,
      saldoActual,
      saldoMasNuevo,
      saldoRutas,
      ultimoSaldo,
    };
  }, [gastosHoy, rutasData, usuarioRuta]);
};

export const useCierreCajaHandler = ({
  infoClientes,
  prestamoDelDia,
  totalAbonos,
  usuarioRuta,
  userData,
}: UseCierreCajaHandlerParams): UseCierreCajaHandlerReturn => {
  const [isConfirmarCierreCajaOpen, setIsConfirmarCierreCajaOpen] = useState(false);
  const [isTablaCierresCajaOpen, setIsTablaCierresCajaOpen] = useState(false);
  const [isClosingCaja, setIsClosingCaja] = useState(false);
  const queryClient = useQueryClient();
  const canUseCierreCaja = Boolean(!userData && usuarioRuta?.adminUid && usuarioRuta?.uid);
  const isCloseCajaDisabled = !canUseCierreCaja || infoClientes === null;

  const handleOpenConfirmarCierreCaja = useCallback(() => {
    if (!canUseCierreCaja || infoClientes === null) {
      return;
    }

    setIsConfirmarCierreCajaOpen(true);
  }, [canUseCierreCaja, infoClientes]);

  const handleCloseConfirmarCierreCaja = useCallback(() => {
    if (isClosingCaja) {
      return;
    }

    setIsConfirmarCierreCajaOpen(false);
  }, [isClosingCaja]);

  const handleOpenTablaCierresCaja = useCallback(() => {
    if (!canUseCierreCaja) {
      return;
    }

    setIsTablaCierresCajaOpen(true);
  }, [canUseCierreCaja]);

  const handleCloseTablaCierresCaja = useCallback(() => {
    setIsTablaCierresCajaOpen(false);
  }, []);

  const handleConfirmarCierreCaja = useCallback(async () => {
    if (!canUseCierreCaja || !usuarioRuta) {
      toast.error("No se pudo cerrar la caja");
      return;
    }

    if (infoClientes === null) {
      toast.error("Los datos de clientes aun no estan listos");
      return;
    }

    try {
      setIsClosingCaja(true);

      const db = getFirestore();
      const resultado = await guardarCierreCaja({
        cerradoPor: auth.currentUser?.email ?? usuarioRuta.nombreRuta ?? null,
        clientes: infoClientes,
        cobrosDelDia: totalAbonos,
        db,
        origen: "manual",
        prestamosDelDia: prestamoDelDia,
        usuarioRuta,
      });

      if (resultado.created) {
        toast.success("Cierre de caja guardado con exito");
      } else {
        toast.info("La caja de hoy ya fue cerrada");
      }

      // Invalida el historial para que TablaCierresCaja (que se abre justo
      // debajo) muestre el cierre recien creado en vez de una lista cacheada
      // de antes de esta transaccion.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cierresCajaHistorial(usuarioRuta.adminUid, usuarioRuta.uid),
      });

      setIsConfirmarCierreCajaOpen(false);
      setIsTablaCierresCajaOpen(true);
    } catch (error) {
      console.error("Error al guardar el cierre de caja:", error);
      toast.error("No se pudo cerrar la caja");
    } finally {
      setIsClosingCaja(false);
    }
  }, [canUseCierreCaja, infoClientes, prestamoDelDia, queryClient, totalAbonos, usuarioRuta]);

  return {
    canUseCierreCaja,
    handleCloseConfirmarCierreCaja,
    handleCloseTablaCierresCaja,
    handleConfirmarCierreCaja,
    handleOpenConfirmarCierreCaja,
    handleOpenTablaCierresCaja,
    isCloseCajaDisabled,
    isClosingCaja,
    isConfirmarCierreCajaOpen,
    isTablaCierresCajaOpen,
  };
};
